import {Component, EventEmitter, HostBinding, Input, Output, ViewChild} from '@angular/core';
import {StrainRegistryEntry} from "@app/strain/registry/models/strain-registry-entry.model";
import {MessageType} from '@components/message/message-type.enum';
import {ToolSet} from '@app/tool/model/toolSet.model';
import {SampleBoardItem, SampleBoardItemStatus} from "@app/strain/sample-boards/models/sample-board-item.model";
import {User} from '@models/user.model';
import { RegistryItemSampleBoard } from "@app/strain/materials/models/registry-item-sample-board";
import { ModalComponent } from "@app/shared/components";
import { PartNumber } from "@models/partNumber.model";
import {
    StrainEquivalentPartNumbersModalComponent
} from "@app/strain/common/components/strain-equivalent-part-numbers-modal/strain-equivalent-part-numbers-modal.component";
import {
    StrainUpdateMainPartNumberModalComponent
} from "@app/strain/common/components/strain-update-main-part-number-modal/strain-update-main-part-number-modal.component";

@Component({
    selector: 'app-strain-configuration-card',
    templateUrl: './strain-configuration-card.component.html',
    styleUrls: ['./strain-configuration-card.component.scss']
})
export class StrainConfigurationCardComponent {
    @ViewChild('sampleBoardsModal', { static: false }) sampleBoardsModal: ModalComponent;
    @ViewChild('equivalentPartNumbersModal', { static: false })
    equivalentPartNumbersModal: StrainEquivalentPartNumbersModalComponent;
    @ViewChild('updateMainPartNumberModal', { static: false })
    updateMainPartNumberModal: StrainUpdateMainPartNumberModalComponent;

    @Input() isLoadingDetails = false;
    @Input() isLoadingTools = false;
    @Input() entry: StrainRegistryEntry;
    @Input() tools: any;
    @Input() canRenderWarnings = false;
    @Input() participants: User[] = null;
    @Input() showStock = true;
    @Input() showParticipants = false;
    @Input() strainEquivalentPartNumbers: PartNumber[] = [];
    @Input() canEditEquivalent = false;
    @Input() isEquivalentUpdateInProgress = false;
    @Output() openSampleBoard = new EventEmitter<number>();
    @Output() updateStrainEquivalentPartNumbers = new EventEmitter<number[]>();
    @Output() updateMainPartNumber = new EventEmitter<number>();

    protected readonly MessageType = MessageType;

    @HostBinding('class.show-stock') get showStockLayout() {
        return this.showStock && !this.showParticipants;
    }

    @HostBinding('class.show-participants') get showParticipantsLayout() {
        return this.showParticipants && !this.showStock;
    }

    @HostBinding('class.show-stock-and-participants') get showStockAndParticipantsLayout() {
        return this.showStock && this.showParticipants;
    }

    canRenderSampleBoardWarning() {
        return this.entry && !this.entry.isStockSufficient && this.canRenderWarnings;
    }

    canRenderToolsWarning() {
        if (this.tools && this.tools.length > 0 && this.canRenderWarnings) {
            return this.tools.every(item => {
                if (item instanceof ToolSet) {
                    return item.tools.every(tool => tool.name.indexOf('TOOL_FOR_STRAIN_ITEM_ID') !== -1)
                } else {
                    return item.name.indexOf('TOOL_FOR_STRAIN_ITEM_ID') !== -1;
                }
            })
        }
        return false;
    }

    canRenderMaxValueWarning() {
        return this.entry?.measurements?.total > 0
            && this.entry?.measurements?.items[0].maxValue > this.entry?.limitAcceptedByClient
            && this.canRenderWarnings;
    }

    get requiredSampleBoardsCount() {
        return (this.entry?.sampleBoards || [])
            .reduce((total, board) => total + (Number(board?.requiredQuantity) || 0), 0);
    }

    get spareSampleBoardsCount() {
        return (this.entry?.sampleBoards || [])
            .reduce((total, board) => total + (Number(board?.spareQuantity) || 0), 0);
    }

    get availableSampleBoardsCount() {
        return this.stockItems.filter(item => item.status === SampleBoardItemStatus.AvailableInStock)?.length;
    }

    get stockItems(): SampleBoardItem[] {
        return (this.entry?.sampleBoards || [])
            .reduce((acc, board) => acc.concat(board?.sampleBoard?.items || []), [] as any[])
            .map(item => new SampleBoardItem(item));
    }

    get sampleBoards(): RegistryItemSampleBoard[] {
        return (this.entry?.sampleBoards || [])
            .filter(board => !!board?.sampleBoard?.id);
    }

    onOpenSampleBoard(board: RegistryItemSampleBoard): void {
        const sampleBoardId = board?.sampleBoard?.id;
        if (!sampleBoardId) {
            return;
        }
        this.openSampleBoard.emit(sampleBoardId);
        this.closeSampleBoardsModal();
    }

    openSampleBoardsModal(event?: MouseEvent): void {
        event?.stopPropagation();
        this.sampleBoardsModal?.open();
    }

    closeSampleBoardsModal(): void {
        this.sampleBoardsModal?.close();
    }

    openEquivalentPartNumbersModal(event?: MouseEvent): void {
        this.equivalentPartNumbersModal?.open(event);
    }

    onSaveEquivalentPartNumbers(equivalentPartNumberIds: number[]): void {
        this.updateStrainEquivalentPartNumbers.emit(equivalentPartNumberIds);
    }

    openUpdateMainPartNumberModal(event?: MouseEvent): void {
        this.updateMainPartNumberModal?.open(event);
    }

    onSaveMainPartNumber(partNumberId: number): void {
        this.updateMainPartNumber.emit(partNumberId);
    }

    canUpdateMainPartNumber(): boolean {
        return this.canEditEquivalent && !!this.entry?.partNumber?.strainEquivalentPartNumbers?.length;
    }

    getBomStatusStyle(bomStatus: string) {
        if (bomStatus === "40") {
            return 'green-background';
        } else if (bomStatus === "35" || bomStatus === "50") {
            return 'red-background';
        } else {
            return 'transparent-background';
        }
    }
}
